import qs from 'query-string';
import { useCallback, useEffect, useRef, useState } from 'react';
import useBaseAPI from './useBaseAPI';

const useProjectAPI = () => {
  const { client, isLoading } = useBaseAPI();

  const getProjects = async (query) => {
    try {
      const response = await client('get', `/project?${qs.stringify(query)}`);
      return response?.data;
    } catch (error) {
      return new Promise((resolve, reject) => {
        reject(error);
      });
    }
  };

  const getProject = async (projectId) => {
    try {
      const response = await client('get', `/project/${projectId}`);
      return response?.data;
    } catch (error) {
      return new Promise((resolve, reject) => {
        reject(error);
      });
    }
  };

  const addProject = async (payload) => {
    try {
      const response = await client('post', '/project', payload);
      return response;
    } catch (error) {
      return new Promise((resolve, reject) => {
        reject(error);
      });
    }
  };

  const updateProject = async (projectId, payload) => {
    try {
      const response = await client('post', `/project/${projectId}`, payload);
      return response;
    } catch (error) {
      return new Promise((resolve, reject) => {
        reject(error);
      });
    }
  };

  const deleteProject = async (projectId) => {
    try {
      const response = await client('delete', `/project/${projectId}`);
      return response?.success;
    } catch (error) {
      return new Promise((resolve, reject) => {
        reject(error);
      });
    }
  };

  const getProjectsPaginated = async (query) => {
    try {
      const response = await client('get', `/project?${qs.stringify(query)}`);
      return response?.data;
    } catch (error) {
      return new Promise((resolve, reject) => {
        reject(error);
      });
    }
  };

  return {
    getProjects,
    getProject,
    addProject,
    updateProject,
    deleteProject,
    getProjectsPaginated,
    isLoading,
  };
};

export default useProjectAPI;

export const useProjectHook = () => {
  const [projects, setProjects] = useState();
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [searchString, setSearchString] = useState('');
  const [isPaginationLoading, setIsPaginationLoading] = useState(false);
  const [customerId, setCustomerId] = useState();

  const { getProjectsPaginated } = useProjectAPI();

  const isInitialLoad = useRef(true);

  useEffect(() => {
    fetchProjects();
  }, [page, customerId]);

  useEffect(() => {
    if (isInitialLoad.current) return;

    let searchStringTimeoutId = null;

    searchStringTimeoutId = setTimeout(() => {
      setProjects(undefined);
      setPage(1);
      if (page === 1) fetchProjects();
    }, 1000);

    return () => clearTimeout(searchStringTimeoutId);
  }, [searchString]);

  useEffect(() => {
    if (isInitialLoad.current) {
      isInitialLoad.current = false;
    }
  }, []);

  const fetchProjects = async () => {
    setIsPaginationLoading(true);
    const query = { search: searchString, page, customerId, limit: 50 };
    const projectResponse = await getProjectsPaginated(query);
    setProjects(page === 1 ? projectResponse.data : [...projects, ...projectResponse.data]);
    setHasMore(projectResponse?.haveMore);
    setIsPaginationLoading(false);
  };

  const observer = useRef();

  const lastElementRef = useCallback((node) => {
    if (isPaginationLoading) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasMore) {
        setIsPaginationLoading(true);
        setPage((prevPageNumber) => prevPageNumber + 1);
      }
    });
    if (node) observer.current.observe(node);
  });

  return {
    projects,
    searchString,
    setSearchString,
    setPage,
    lastElementRef,
    isPaginationLoading,
    setCustomerId,
    customerId,
    fetchProjects,
    setProjects,
  };
};
